import type { Injector } from '@angular/core';
import { inject, linkedSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import {
  patchState,
  signalStore,
  withComputed,
  withLinkedState,
  withMethods,
  withProps,
  withState,
} from '@ngrx/signals';
import { chain } from 'lodash';
import { Subject } from 'rxjs';
import type { TelematicsItem } from '../../../../common/interfaces/telematics/telematics-item';
import { TelematicsStore } from '../../../../common/stores/telematics.store';
import { SwapStore } from './swap.store';

interface MapState {
  showLoading: boolean;
  showOnlySelectedVehicle: boolean;
  showRouteCalculator: boolean;
  showService: boolean;
  showServiceDetails: boolean;
}

export const MapStore = signalStore(
  { providedIn: 'root' },
  withState<MapState>({
    showLoading: false,
    showOnlySelectedVehicle: true,
    showRouteCalculator: false,
    showService: false,
    showServiceDetails: false,
  }),
  withProps(() => ({
    _telematicsStore: inject(TelematicsStore),
  })),
  withProps(() => ({
    _fitContent$: new Subject<void>(),
    _fitVehicle$: new Subject<{ id: string }>(),
  })),
  withProps((store) => ({
    fitContent$: store._fitContent$.asObservable(),
    fitVehicle$: store._fitVehicle$.asObservable(),
  })),
  withLinkedState((store) => ({
    showPanel: linkedSignal<boolean>(
      () =>
        !store._telematicsStore.query() ||
        !store._telematicsStore.filters() ||
        true,
    ),
    showInactive: linkedSignal<boolean, boolean>({
      source: () => {
        const data = store._telematicsStore.data();
        return !!data.length && data.every((item) => !item.telemetry);
      },
      computation: (hasOnlyInactive, previous) =>
        hasOnlyInactive || (previous?.value ?? false),
    }),
    showLegend: linkedSignal<boolean, boolean>({
      source: () => store._telematicsStore.showFilters(),
      computation: (showFilters, previous) => {
        if (showFilters) return false;
        return previous?.value ?? false;
      },
    }),

    selectedVehicle: linkedSignal<
      Map<string, TelematicsItem>,
      TelematicsItem | null
    >({
      source: () => store._telematicsStore.unsortedData(),
      computation: (data, previous) => {
        if (!previous?.value) return null;
        return data.get(previous.value.uuid) ?? null;
      },
    }),
  })),
  withComputed((store) => ({
    data: () => {
      const data = store._telematicsStore.data();
      const showInactive = store.showInactive();

      return chain(data)
        .map((item) => ({ ...item, showInactiveToggle: false }))
        .groupBy((item) => (item.telemetry ? 'active' : 'inactive'))
        .thru((groups) => {
          const active = groups['active'] ?? [];
          const inactive = groups['inactive'] ?? [];

          const lastActive = active.at(-1);
          if (lastActive) lastActive.showInactiveToggle = !!inactive.length;

          return {
            active,
            inactive,
            list: showInactive ? [...active, ...inactive] : active,
          };
        })
        .value();
    },
  })),
  withProps((store) => ({
    data$: toObservable(store.data),
  })),
  withMethods((store) => ({
    closePanels(
      panels: { filters?: true; legend?: true } = {
        filters: true,
        legend: true,
      },
    ): void {
      if (panels.filters) store._telematicsStore.toggleShowFilters(false);
      if (panels.legend) this.toggleShowLegend(false);
    },

    closeFeatures(
      injector: Injector,
      features: { routeCalculator?: true; swapPanel?: true; service?: true } = {
        routeCalculator: true,
        swapPanel: true,
        service: true,
      },
    ): void {
      if (features.routeCalculator) this.toggleShowRouteCalculator(false);
      if (features.service) this.toggleShowService(false);
      if (features.swapPanel) {
        injector.get(SwapStore).toggleShowSwapPanel(false);
      }
    },

    fitContent(): void {
      store._fitContent$.next();
    },

    fitVehicle(id: string): void {
      store._fitVehicle$.next({ id });
    },

    selectVehicle(item: TelematicsItem | null): void {
      patchState(store, { selectedVehicle: item });
      this.closePanels();

      setTimeout(() => this.fitContent());
    },

    setShowLoading(value: boolean) {
      patchState(store, { showLoading: value });
    },

    setShowServiceDetails(show: boolean): void {
      patchState(store, { showServiceDetails: show });
    },

    toggleShowInactive(force?: boolean): void {
      patchState(store, (state) => ({
        showInactive: force ?? !state.showInactive,
      }));
    },

    toggleShowPanel(force?: boolean): void {
      patchState(store, (state) => ({ showPanel: force ?? !state.showPanel }));
    },

    toggleShowOnlySelectedVehicle(force?: boolean): void {
      patchState(store, (state) => ({
        showOnlySelectedVehicle: force ?? !state.showOnlySelectedVehicle,
      }));
    },

    toggleShowLegend(force?: boolean): void {
      patchState(store, (state) => ({
        showLegend: force ?? !state.showLegend,
      }));

      if (store.showLegend()) store._telematicsStore.toggleShowFilters(false);
    },

    toggleShowRouteCalculator(force?: boolean): void {
      patchState(store, (state) => ({
        showRouteCalculator: force ?? !state.showRouteCalculator,
      }));
    },

    toggleShowService(force?: boolean): void {
      patchState(store, (state) => ({
        showService: force ?? !state.showService,
      }));
    },

    toggleVehicle(item: TelematicsItem): void {
      patchState(store, (state) => ({
        selectedVehicle:
          state.selectedVehicle?.uuid === item.uuid ? null : item,
      }));
      this.closePanels();

      setTimeout(() => this.fitContent());
    },
  })),
);
